import {
  Component,
  OnChanges,
  OnDestroy,
  Input,
  OnInit,
  ViewChild,
  ElementRef
} from '@angular/core';
import { debounce, first, forEach, get, isEmpty, isString, merge } from 'lodash';
import { map, catchError, finalize, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subscription, EMPTY, Subject } from 'rxjs';
import { ApiService, MapAttribution } from '@nrwl/common-lib';
import { UntypedFormControl } from '@angular/forms';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { UserService } from '@nrwl/common-lib';
import * as L from 'leaflet';

const tileUrls = {
  light:
    'https://api.mapbox.com/styles/v1/viavirubix/ckbl7t89i06pq1jp2e0z57anu/tiles/256/{z}/{x}/{y}?access_token=pk.eyJ1IjoidmlhdmlydWJpeCIsImEiOiI2ZTU4YzQ0OGNmZmY5NmNkOGFiMWU2YWQzMzUwNzAyMCJ9.xivWYQccbSrEdOONaiLL3Q',
  dark: 'https://api.mapbox.com/styles/v1/viavirubix/ckbl7uaqz06r61iny9zs6yb4q/tiles/256/{z}/{x}/{y}?access_token=pk.eyJ1IjoidmlhdmlydWJpeCIsImEiOiI2ZTU4YzQ0OGNmZmY5NmNkOGFiMWU2YWQzMzUwNzAyMCJ9.xivWYQccbSrEdOONaiLL3Q'
};

@Component({
  selector: 'location-settings',
  templateUrl: './location.settings.template.html',
  styleUrls: ['./location.settings.style.scss']
})
export class LocationSettingsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() parentOutput: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  @Input() parent: string = null;
  @Input() defaultLocation: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  @Input() config: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  @Input() dataSource: BehaviorSubject<any>; //eslint-disable-line @typescript-eslint/no-explicit-any
  @Input() key: string;
  @Input() step: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  @ViewChild('locationInput', { static: true }) locationInput: UntypedFormControl;
  @ViewChild('mapLocation', { static: true }) mapLocation: ElementRef;

  mapAttribution: MapAttribution = new MapAttribution(L);
  mapOptions: any = {}; //eslint-disable-line @typescript-eslint/no-explicit-any
  map: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  selectedItem: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  itemSelected: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  selectedPoint: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  searchText: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  filterText: string;
  changeFriendlyName = true;
  isLoading = false;
  dataLocation: any; //eslint-disable-line @typescript-eslint/no-explicit-any
  filteredCoordinationPoints: Observable<any>; //eslint-disable-line @typescript-eslint/no-explicit-any

  subscription: Subscription;
  locationSubscription: Subscription;
  private readonly unsubscribe: Subject<void> = new Subject();

  constructor(private readonly apiService: ApiService, private readonly userService: UserService) {
    this.mapAttribution = new MapAttribution(L);
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
    if (this.locationSubscription) {
      this.locationSubscription.unsubscribe();
      this.locationSubscription = null;
    }
  }

  ngOnInit() {
    this.initMap(this.mapLocation.nativeElement);

    this.locationSubscription = this.locationInput.valueChanges.subscribe(changes => {
      this.getCoordinatePointsByLocationQuery(changes)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(
          response => {
            this.filteredCoordinationPoints = response.features;
          },
          () => {}
        );
    });

    setTimeout(() => {
      this.map.invalidateSize();
    }, 100);
    this._repopulateLocationDetails();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  initMap(element: any): void {
    this.mapOptions = {
      tilesUrl: tileUrls[this.userService.getDefaultTheme()],
      defaultView: {
        coords: [0, 0],
        zoomLevel: 1.5
      },
      icons: {
        inProgress:
          // tslint:disable-next-line:max-line-length
          'data:image/svg+xml;base64,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',
        found:
          // tslint:disable-next-line:max-line-length
          'data:image/svg+xml;base64,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'
      }
    };

    this.map = L.map(element, {
      scrollWheelZoom: false,
      zoomControl: true,
      minZoom: 1,
      trackResize: true,
      worldCopyJump: true,
      attributionControl: false,
      maxBounds: L.latLngBounds(L.latLng([-60, -180]), L.latLng([90, 180])),
      maxBoundsViscosity: 1.0
    }).setView(this.mapOptions.defaultView.coords, this.mapOptions.defaultView.zoomLevel);
    const tilesUrl =
      window.location &&
      window.location.href &&
      window.location.href.toLowerCase().indexOf('testmode') >= 0
        ? '/images/na.png'
        : this.mapOptions.tilesUrl;
    L.tileLayer(tilesUrl, {
      noWrap: true,
      continuousWorld: false,
      bounds: [
        [-90, -180],
        [90, 180]
      ]
    }).addTo(this.map);

    this.mapAttribution.addTo(this.map);

    this.map.zoomControl.setPosition('bottomright');

    this.map.on(
      'click',
      debounce(event => {
        this._onMapClick(event.latlng);
      }, 600)
    );

    //Put point on map if it is already defined
    if (!isEmpty(this.selectedItem)) {
      this.setLocation(this.selectedItem);
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  splitLocation(locationName: any): any {
    if (locationName) {
      const lastIndex = locationName.lastIndexOf(',');
      return {
        name: lastIndex > 0 ? locationName.substring(0, lastIndex).trim() : locationName,
        country: lastIndex > 0 ? locationName.substring(lastIndex + 1).trim() : ''
      };
    } else {
      return {
        name: '',
        country: ''
      };
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  _onMapClick(latlng: any): void {
    this.getCoordinatePointsByLocationQuery(`${latlng.lng},${latlng.lat}`, 1)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          const item = first(response.features);
          this.searchText = item ? merge({}, item, { noZoom: false }) : null;
          this.selectedItem = this.searchText;
          this.parentOutput.location = {
            lat: `${latlng.lat}`,
            lon: `${latlng.lng}`
          };
          if (!isEmpty(item) && get(item, 'context')) {
            this.parentOutput.description = item.place_name;
            forEach(get(item, 'context'), data => {
              if (data.id.includes('place')) {
                this.parentOutput.location.city = data.text;
              } else if (data.id.includes('region')) {
                this.parentOutput.location.state = data.text;
              } else if (data.id.includes('country')) {
                this.parentOutput.location.country = data.text;
              }
            });
          }
          this.setLocation(this.selectedItem);
        },
        () => {}
      );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  _selectOnePoint(selectedItem: any, icon: any): void {
    if (this.selectedPoint) {
      this.map.removeLayer(this.selectedPoint);
    }
    this.selectedPoint = this._pointToMap(selectedItem, icon);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  setRegionAndLocation(selectedItem) {
    this._selectOnePoint(selectedItem, this.mapOptions.icons.found);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  _pointToMap(selectedItem, iconUrl): any {
    const marker = L.marker(selectedItem, { icon: this._getIcon(iconUrl) });
    this.map.addLayer(marker);

    if (!this.selectedItem.noZoom) {
      this.map.setView(selectedItem, 13);
    }

    return marker;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  _getIcon(iconUrl) {
    return L.icon({
      iconUrl,
      iconSize: [24, 24], // size of the icon
      iconAnchor: [12, 12] // point of the icon which will correspond to marker's location
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  resetOutput() {
    this.parentOutput.region = null;
    this.parentOutput.location = undefined;
    this.parentOutput.orginalLocation = undefined;
    this.selectedItem = null;
    this.map.setView(this.mapOptions.defaultView.coords, this.mapOptions.defaultView.zoomLevel);
    if (this.selectedPoint) {
      this.map.removeLayer(this.selectedPoint);
    }
    this.resetSelectedItem();
  }

  setLocationFromSelection(): void {
    if (isString(this.searchText)) {
      this.selectedItem = null;
    } else {
      this.selectedItem = this.searchText;
    }
    const latlng = this.setLocation(this.selectedItem);
    if (latlng) {
      this.setOutput(latlng);
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  setOutput(latlng) {
    this.getCoordinatePointsByLocationQuery(`${latlng.lng},${latlng.lat}`, 1)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          const item = first(response.features);
          this.parentOutput.location = {
            lat: `${latlng.lat}`,
            lon: `${latlng.lng}`
          };
          if (!isEmpty(item) && get(item, 'context')) {
            this.parentOutput.description = item.place_name;
            forEach(get(item, 'context'), data => {
              if (data.id.includes('place')) {
                this.parentOutput.location.city = data.text;
              } else if (data.id.includes('region')) {
                this.parentOutput.location.state = data.text;
              } else if (data.id.includes('country')) {
                this.parentOutput.location.country = data.text;
              }
            });
          }
        },
        () => {}
      );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  getPlaceName(value) {
    if (value && value.place_name) {
      return value.place_name;
    }
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  setLocation(selectedItem) {
    if (isEmpty(selectedItem)) {
      this.resetOutput();
      return;
    }
    const coords = selectedItem.geometry.coordinates;
    const latLon = L.latLng(coords[1], coords[0]);
    latLon.place_name = selectedItem.place_name;
    this.setRegionAndLocation(latLon);
    return latLon;
  }

  //Using MapBox
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  getCoordinatePointsByLocationQuery(location, limit = 10): Observable<any> {
    //tslint:disable-next-line:max-line-length
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${location}.json?limit=${limit}&access_token=pk.eyJ1IjoidmlhdmlydWJpeCIsImEiOiI2ZTU4YzQ0OGNmZmY5NmNkOGFiMWU2YWQzMzUwNzAyMCJ9.xivWYQccbSrEdOONaiLL3Q`;
    return this.apiService.get$(url, {});
  }

  /**
   * Please note this event is triggered only for one-way bindings (<)
   */
  ngOnChanges() {
    setTimeout(() => {
      this._repopulateLocationDetails();
    }, 0);
  }

  _repopulateLocationDetails(): void {
    if (this.parentOutput.location?.lat && this.parentOutput.location?.lon) {
      this.itemSelected = false;
      this.getCoordinatePointsByLocationQuery(
        `${this.parentOutput.location.lon},${this.parentOutput.location.lat}`,
        1
      )
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(
          response => {
            const item = first(response.features);
            this.searchText = item ? merge({}, item, { noZoom: true }) : null;
            this.setLocationFromSelection();
          },
          () => {}
        );
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  onItemClick(item: any): void {
    this.itemSelected = true;
    item.selected = true;
    this.updateOutput(item);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  updateOutput(item: any): any {
    const location = item.location;
    const url = '/geo/v1/find/' + `${location.coordinate.lat}/${location.coordinate.lon}`;
    this.isLoading = true;
    this.apiService.get$(url, {}).pipe(
      map(response => {
        if (response.status === 200) {
        }
      }),
      catchError(() => {
        return EMPTY;
      }),
      finalize(() => {
        this.isLoading = false;
      })
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  filterLocation(filterText: any): void {
    forEach(this.dataLocation, data => {
      const locationName = data.location.name.toLowerCase();
      data.hidden = !(isEmpty(filterText) || locationName.indexOf(filterText.toLowerCase()) > -1);
    });
  }

  tabChanged(event: MatTabChangeEvent): void {
    this.onChangeTab(event.index);
  }

  onChangeTab(index: number): void {
    if (index === 1) {
      setTimeout(() => {
        this.map.invalidateSize();
      }, 100);
    }
    this.resetOutput();
  }

  resetSelectedItem(): void {
    this.itemSelected = false;
    forEach(this.dataLocation, data => {
      data.selected = false;
    });
  }

  updateFriendlyName(): void {
    this.changeFriendlyName = !!isEmpty(this.parentOutput.location);
  }
}
